import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import { gamificacionService } from '../services/gamificacionService';
import { perfilService } from '../services/perfilService';
import { useUIStore } from '../store/uiStore';
import { intusLogoutUrl, redirectToIntusLogout } from '../utils/intusRedirect';
import { 

  Settings, 
  Shield, 

  Download,
  LogOut,

  Camera,
  Calendar,
  Award,
  TrendingUp,
  KeyRound,
  CheckCircle2
} from 'lucide-react';

export default function PerfilPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { addToast } = useUIStore();
  // P4: el switch de "Ver mi progreso" lee y escribe el mismo estado que usa el
  // dashboard, asi que los dos quedan sincronizados por el store y no por props.
  const gamificacionVisible = useUIStore((s) => s.gamificacionVisible);
  const setGamificacionVisible = useUIStore((s) => s.setGamificacionVisible);
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'estadisticas' | 'configuracion' | 'privacidad'>('estadisticas');

  // 2FA real (Fase 6.4)
  const [twoFactorActivado, setTwoFactorActivado] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState<{ secret: string; qrCode: string } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isTwoFactorBusy, setIsTwoFactorBusy] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await gamificacionService.getEstadisticas();
      setStats(data);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar las estadísticas',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    const { refreshToken } = useAuthStore.getState();
    if (refreshToken) {
      try {
        await authService.logout(refreshToken);
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    logout();
    // Cierre completo: además de la sesión de la app, se cierra la sesión
    // del paciente en Vita360 (logout-remote) y se aterriza en la bienvenida
    // del portal paciente (localhost:8000/bienvenida-paciente).
    if (intusLogoutUrl()) {
      redirectToIntusLogout();
    } else {
      navigate('/login');
    }
  };

  const handleDownloadData = async () => {
    try {
      // apiClient agrega el Authorization (Fase 6.3): antes esto era un
      // fetch crudo que devolvía 401.
      const blob = await perfilService.descargarDatos();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mis-datos-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      addToast({
        type: 'success',
        title: 'Datos descargados',
        message: 'Tus datos han sido descargados exitosamente',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudieron descargar los datos',
      });
    }
  };

  const handleActivar2FA = async () => {
    setIsTwoFactorBusy(true);
    try {
      const setup = await perfilService.activar2FA();
      setTwoFactorSetup(setup);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudo iniciar la activación de 2FA',
      });
    } finally {
      setIsTwoFactorBusy(false);
    }
  };

  const handleConfirmar2FA = async () => {
    if (twoFactorCode.length !== 6) {
      addToast({
        type: 'error',
        title: 'Código inválido',
        message: 'El código debe tener 6 dígitos',
      });
      return;
    }
    setIsTwoFactorBusy(true);
    try {
      const { valido } = await perfilService.validar2FA(twoFactorCode);
      if (valido) {
        setTwoFactorActivado(true);
        setTwoFactorSetup(null);
        setTwoFactorCode('');
        addToast({
          type: 'success',
          title: '2FA activada',
          message: 'Tu cuenta está protegida con autenticación de dos factores',
        });
      } else {
        addToast({
          type: 'error',
          title: 'Código incorrecto',
          message: 'Verifica el código de tu app de autenticación',
        });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudo validar el código',
      });
    } finally {
      setIsTwoFactorBusy(false);
    }
  };

  const handleDesactivar2FA = async () => {
    setIsTwoFactorBusy(true);
    try {
      await perfilService.desactivar2FA();
      setTwoFactorActivado(false);
      addToast({
        type: 'success',
        title: '2FA desactivada',
        message: 'La autenticación de dos factores quedó desactivada',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudo desactivar 2FA',
      });
    } finally {
      setIsTwoFactorBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner h-12 w-12"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="gradient-wellness rounded-2xl p-6 text-white">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold">
              {user?.nombre?.charAt(0) || 'U'}
            </div>
            <button className="absolute bottom-0 right-0 bg-white rounded-full p-2 text-primary hover:bg-white/90 transition-colors">
              <Camera className="h-4 w-4" />
            </button>
          </div>
          <div>
            <h1 className="text-2xl font-bold">{user?.nombre || 'Usuario'}</h1>
            <p className="text-white/90">{user?.email}</p>
            <p className="text-white/80 text-sm">{user?.grado} • {user?.rol}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-secondary rounded-lg p-1">
        <button
          onClick={() => setActiveTab('estadisticas')}
          className={`flex-1 py-2 px-4 rounded-md transition-colors ${
            activeTab === 'estadisticas' ? 'bg-background shadow' : 'hover:bg-accent'
          }`}
        >
          <span className="flex items-center justify-center space-x-2">
            <TrendingUp className="h-4 w-4" />
            <span>Estadísticas</span>
          </span>
        </button>
        <button
          onClick={() => setActiveTab('configuracion')}
          className={`flex-1 py-2 px-4 rounded-md transition-colors ${
            activeTab === 'configuracion' ? 'bg-background shadow' : 'hover:bg-accent'
          }`}
        >
          <span className="flex items-center justify-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>Configuración</span>
          </span>
        </button>
        <button
          onClick={() => setActiveTab('privacidad')}
          className={`flex-1 py-2 px-4 rounded-md transition-colors ${
            activeTab === 'privacidad' ? 'bg-background shadow' : 'hover:bg-accent'
          }`}
        >
          <span className="flex items-center justify-center space-x-2">
            <Shield className="h-4 w-4" />
            <span>Privacidad</span>
          </span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'estadisticas' && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-4 gap-4">
            <StatCard icon={Award} label="Nivel" value={stats?.nivel || 'Explorador'} />
            <StatCard icon={TrendingUp} label="Puntos" value={stats?.puntos || 0} />
            <StatCard icon={Calendar} label="Racha" value={`${stats?.rachaDias || 0} días`} />
            <StatCard icon={Award} label="Logros" value={stats?.ejerciciosCompletados || 0} />
          </div>

          <div className="bg-card rounded-xl p-6 border">
            <h3 className="font-semibold mb-4">Historial de Actividad</h3>
            <div className="space-y-3">
              <ActivityItem title="Ejercicio completado" time="Hace 2 horas" />
              <ActivityItem title="Evaluación finalizada" time="Hace 3 días" />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'configuracion' && (
        <div className="space-y-6">
          <div className="bg-card rounded-xl p-6 border">
            <h3 className="font-semibold mb-4">Notificaciones</h3>
            <div className="space-y-4">
              <NotificationItem label="Recordatorios de ejercicios" defaultChecked />
              <NotificationItem label="Actualizaciones de comunidad" defaultChecked />
              <NotificationItem label="Alertas de riesgo" defaultChecked />
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border">
            <h3 className="font-semibold mb-4">Preferencias</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span>Modo oscuro</span>
                {/* Antes este boton no tenia `onClick` ni `aria-checked`: era un
                    interruptor que NO hacia nada. El tema si se cambiaba desde el
                    header, asi que el duplicado dead de aqui pura y simplemente. */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={theme === 'dark'}
                  aria-label="Modo oscuro"
                  onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    theme === 'dark' ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    aria-hidden
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      theme === 'dark' ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* P4 del plan de cierre. La gamificacion es opt-in desde la Fase 4
                  (la tarjeta de nivel y el historial ocultos por defecto), y
                  el estado vivia en `uiStore` sin ninguna UI que lo alcanzara:
                  la decision de producto existia pero era inaccionable. Aqui se
                  conecta. Persiste en `ui-storage`, sin llamada al backend. */}
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <span>Ver mi progreso</span>
                  <p className="text-xs text-v-on-surface-variant mt-0.5">
                    Muestra tu nivel y lo que has hecho en el inicio.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={gamificacionVisible}
                  aria-label="Ver mi progreso"
                  onClick={() => setGamificacionVisible(!gamificacionVisible)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    gamificacionVisible ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    aria-hidden
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      gamificacionVisible ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span>Idioma</span>
                <select className="border rounded-lg px-3 py-2">
                  <option>Español</option>
                  <option>English</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'privacidad' && (
        <div className="space-y-6">
          <div className="bg-card rounded-xl p-6 border">
            <h3 className="font-semibold mb-4">Seguridad</h3>
            <div className="space-y-3">
              <button className="w-full flex items-center justify-between p-3 bg-secondary rounded-lg hover:bg-accent transition-colors">
                <span className="flex items-center space-x-3">
                  <Shield className="h-5 w-5" />
                  <span>Cambiar contraseña</span>
                </span>
              </button>
              <button className="w-full flex items-center justify-between p-3 bg-secondary rounded-lg hover:bg-accent transition-colors">
                <span className="flex items-center space-x-3">
                  <Shield className="h-5 w-5" />
                  <span>Cambiar contraseña</span>
                </span>
              </button>

              {twoFactorActivado ? (
                <div className="flex items-center justify-between p-3 bg-v-verde-100 rounded-lg border border-green-200">
                  <span className="flex items-center space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-v-verde-700" />
                    <span className="font-medium text-v-verde-700">2FA activada</span>
                  </span>
                  <button
                    onClick={handleDesactivar2FA}
                    disabled={isTwoFactorBusy}
                    className="text-sm text-destructive hover:underline disabled:opacity-50"
                  >
                    Desactivar
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleActivar2FA}
                  disabled={isTwoFactorBusy}
                  className="w-full flex items-center justify-between p-3 bg-secondary rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                >
                  <span className="flex items-center space-x-3">
                    <KeyRound className="h-5 w-5" />
                    <span>Activar autenticación 2FA</span>
                  </span>
                </button>
              )}

              {twoFactorSetup && (
                <div className="p-4 bg-card border rounded-lg space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <KeyRound className="h-4 w-4" />
                    Configura tu app de autenticación
                  </h4>
                  <p className="text-sm text-v-on-surface-variant">
                    Usa Google Authenticator o similar. Ingresa la clave manual:
                  </p>
                  <code className="block rounded bg-muted p-2 text-xs break-all">{twoFactorSetup.secret}</code>
                  <p className="text-xs text-v-on-surface-variant break-all">O escanea: {twoFactorSetup.qrCode}</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Código de 6 dígitos"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                      className="flex-1 rounded-lg border px-3 py-2 text-sm"
                    />
                    <button
                      onClick={handleConfirmar2FA}
                      disabled={isTwoFactorBusy}
                      className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                    >
                      Confirmar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border">
            <h3 className="font-semibold mb-4">Datos</h3>
            <button
              onClick={handleDownloadData}
              className="w-full flex items-center justify-center space-x-2 p-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Download className="h-5 w-5" />
              <span>Descargar mis datos</span>
            </button>
          </div>

          <div className="bg-card rounded-xl p-6 border">
            <h3 className="font-semibold mb-4">Padres/Tutores</h3>
            <div className="space-y-3">
              <button className="w-full flex items-center justify-between p-3 bg-secondary rounded-lg hover:bg-accent transition-colors">
                <span>Generar código de invitación</span>
              </button>
              <button className="w-full flex items-center justify-between p-3 bg-secondary rounded-lg hover:bg-accent transition-colors">
                <span>Revocar acceso parental</span>
              </button>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 p-3 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }: any) {
  return (
    <div className="bg-card rounded-xl p-4 border">
      <Icon className="h-5 w-5 text-primary mb-2" />
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs text-v-on-surface-variant">{label}</p>
    </div>
  );
}

function ActivityItem({ title, time }: any) {
  return (
    <div className="flex items-center space-x-3 p-3 bg-secondary rounded-lg">
      <Calendar className="h-4 w-4 text-v-on-surface-variant" />
      <div className="flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-v-on-surface-variant">{time}</p>
      </div>
    </div>
  );
}

interface NotificationItemProps {
  label: string;
  defaultChecked?: boolean;
}

function NotificationItem({ label, defaultChecked = false }: NotificationItemProps) {
  // Antes `defaultChecked` se recibia y se ignoraba: el toggle salia siempre
  // encendido y no respondia al toque. Ahora es el estado inicial real.
  const [enabled, setEnabled] = useState(defaultChecked);

  return (
    <div className="flex items-center justify-between gap-4">
      <span>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={label}
        onClick={() => setEnabled((prev) => !prev)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          enabled ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          aria-hidden
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}
